import { buildWitnessUnitTextIndex } from './ledgerTextPreprocessing'
import { getLedgerSafeCellState } from './ledgerSafeCore'
import { normalizeCameraLucidaLeafBudget } from './cameraLucidaLayout'
import { buildStructuralRangeContext, buildStructuralRowOrder } from './structuralRanges'
import type { Witness } from '../store/booksStore'
import type { AnchoredRow, AnchoredRowSceneMetadata } from '../types/anchoredRowTypes'
import type { ChapterCharacterLayer, CharacterLayerNote } from '../types/characterLayerTypes'
import type { ChapterSceneLayer } from '../types/sceneTypes'
import type { ChapterStructuralRangeLayer } from '../types/structuralRangeTypes'
import type {
  CameraLucidaCharacter,
  CameraLucidaCharacterSpan,
  CameraLucidaLeaf,
  CameraLucidaPlateModel,
  CameraLucidaRow,
  CameraLucidaSessionState,
  CameraLucidaStructurePathEntry,
  CameraLucidaWitness,
} from '../types/cameraLucidaTypes'

export const CAMERA_LUCIDA_LEAF_BUDGET = 34
export const CAMERA_LUCIDA_CHARACTER_COLORS = [
  '#0f766e', '#6d28d9', '#be185d', '#3730a3', '#7c2d12', '#155e75',
  '#166534', '#4d7c0f', '#92400e', '#b91c1c', '#1e3a8a', '#86198f',
] as const

const stableHash = (value: string): number => {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

export const getCameraLucidaCharacterColor = (characterId: string): string => (
  CAMERA_LUCIDA_CHARACTER_COLORS[stableHash(characterId) % CAMERA_LUCIDA_CHARACTER_COLORS.length]
  || CAMERA_LUCIDA_CHARACTER_COLORS[0]
)

const countTextUnits = (value: string): number => (
  Array.from(value.replace(/[\u200B-\u200D\uFEFF]/g, '').replace(/\s+/g, '')).length
)

const sceneMetadataForRow = (row: AnchoredRow): AnchoredRowSceneMetadata | null => {
  const value = row.metadata?.scene
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null
}

const normalizeSceneText = (value: string): string => (
  value
    .normalize('NFKC')
    .replace(/[\u064B-\u065F\u0670\u06D6-\u06ED]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLocaleLowerCase()
)

const sceneIdFor = (scene: AnchoredRowSceneMetadata | null, fallback: string): string => {
  if (typeof scene?.sceneId === 'string' && scene.sceneId.trim()) return scene.sceneId.trim()
  if (typeof scene?.sceneOrdinal === 'number') return `scene-${scene.sceneOrdinal}`
  if (typeof scene?.sceneNumber === 'number') return `scene-${scene.sceneNumber}`
  return fallback
}

const parentSceneIdFor = (scene: AnchoredRowSceneMetadata | null): string | undefined => {
  if (!scene) return undefined
  for (const key of ['parentSceneId', 'parent_scene_id', 'parent']) {
    const candidate = scene[key]
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim()
  }
  return undefined
}

const resolveMappingText = (
  row: AnchoredRow,
  witnessId: string,
  lookup: ReturnType<typeof buildWitnessUnitTextIndex>,
): string => {
  const mapping = row.unitMappings.find(candidate => candidate.witnessId === witnessId && !candidate.excluded)
  if (!mapping) return ''
  if (mapping.unitUuid && lookup.has(mapping.unitUuid)) {
    return lookup.get(mapping.unitUuid)?.comparisonText || ''
  }
  return mapping.textSnapshot?.trim() || ''
}

const canonicalSceneMetadataByRow = ({
  rows,
  witnesses,
  sceneLayer,
}: {
  rows: AnchoredRow[]
  witnesses: Witness[]
  sceneLayer?: ChapterSceneLayer
}): Map<string, AnchoredRowSceneMetadata> => {
  const result = new Map<string, AnchoredRowSceneMetadata>()
  if (!sceneLayer?.scene_map?.length) return result

  const sortedRows = [...rows].sort((left, right) => left.position - right.position)
  const rowIndexByUnit = new Map<string, number>()
  const rowIndexByWitnessUnit = new Map<string, number>()
  sortedRows.forEach((row, rowIndex) => row.unitMappings.forEach(mapping => {
    if (!mapping.unitUuid) return
    if (!rowIndexByUnit.has(mapping.unitUuid)) rowIndexByUnit.set(mapping.unitUuid, rowIndex)
    rowIndexByWitnessUnit.set(`${mapping.witnessId}::${mapping.unitUuid}`, rowIndex)
  }))

  const witnessLookups = new Map(witnesses.map(witness => [witness.id, buildWitnessUnitTextIndex(witness)]))
  const findSignatureRow = (signature: string, preferredWitnessId?: string): number | undefined => {
    const normalizedSignature = normalizeSceneText(signature)
    if (!normalizedSignature) return undefined
    const prefix = normalizedSignature.split(' ').slice(0, 6).join(' ')
    const witnessOrder = preferredWitnessId
      ? [preferredWitnessId, ...witnesses.map(witness => witness.id).filter(id => id !== preferredWitnessId)]
      : witnesses.map(witness => witness.id)
    for (const witnessId of witnessOrder) {
      const lookup = witnessLookups.get(witnessId) || new Map()
      for (let rowIndex = 0; rowIndex < sortedRows.length; rowIndex += 1) {
        const text = normalizeSceneText(resolveMappingText(sortedRows[rowIndex], witnessId, lookup))
        if (text.startsWith(normalizedSignature) || (prefix.length > 6 && text.includes(prefix))) return rowIndex
      }
    }
    return undefined
  }

  const definitionById = new Map(sceneLayer.scene_map.map(scene => [scene.scene_id, scene]))
  const orderedDefinitions = [
    ...sceneLayer.scene_order.map(sceneId => definitionById.get(sceneId)).filter(Boolean),
    ...sceneLayer.scene_map.filter(scene => !sceneLayer.scene_order.includes(scene.scene_id)),
  ].filter((scene): scene is ChapterSceneLayer['scene_map'][number] => Boolean(scene))
  const anchorById = new Map(sceneLayer.canonical_anchors.map(anchor => [anchor.scene_id, anchor]))
  const boundaries = orderedDefinitions.flatMap((scene, sceneIndex) => {
    const anchor = anchorById.get(scene.scene_id)
    const unitId = anchor?.reference_unit_id || scene.anchor_unit_id
    const witnessId = anchor?.reference_witness_id
    let rowIndex = unitId && witnessId
      ? rowIndexByWitnessUnit.get(`${witnessId}::${unitId}`)
      : undefined
    if (rowIndex == null && unitId) rowIndex = rowIndexByUnit.get(unitId)
    if (rowIndex == null && anchor?.reference_text_signature) {
      rowIndex = findSignatureRow(anchor.reference_text_signature, witnessId)
    }
    if (rowIndex == null && scene.title) rowIndex = findSignatureRow(scene.title, witnessId)
    if (rowIndex == null && sceneIndex === 0) rowIndex = 0
    return rowIndex == null ? [] : [{ rowIndex, scene, sceneIndex }]
  }).sort((left, right) => left.rowIndex - right.rowIndex || left.sceneIndex - right.sceneIndex)

  if (!boundaries.length) return result
  let boundaryIndex = 0
  sortedRows.forEach((row, rowIndex) => {
    while (boundaryIndex + 1 < boundaries.length && boundaries[boundaryIndex + 1].rowIndex <= rowIndex) {
      boundaryIndex += 1
    }
    const active = boundaries[boundaryIndex]
    if (active.rowIndex > rowIndex) return
    const metadata = active.scene.ui_metadata || {}
    result.set(row.id, {
      sceneId: active.scene.scene_id,
      sceneOrdinal: active.sceneIndex + 1,
      title: active.scene.title || `Scene ${active.sceneIndex + 1}`,
      description: active.scene.description,
      arabicTitle: typeof metadata.arabicTitle === 'string' ? metadata.arabicTitle : undefined,
      parentSceneId: typeof metadata.parentSceneId === 'string' ? metadata.parentSceneId : undefined,
      source: 'chapter-scene-layer',
    })
  })
  return result
}

const createCameraRows = (rows: AnchoredRow[], witnesses: Witness[]): CameraLucidaRow[] => {
  const lookups = new Map(witnesses.map(witness => [witness.id, buildWitnessUnitTextIndex(witness)]))
  return [...rows]
    .sort((left, right) => left.position - right.position)
    .map((row, rowIndex) => {
      const lengths = witnesses.map(witness => countTextUnits(resolveMappingText(
        row,
        witness.id,
        lookups.get(witness.id) || new Map(),
      )))
      const rowMax = Math.max(1, ...lengths)
      const mass = lengths.reduce((total, length) => total + length, 0)
      return {
        id: row.id,
        ordinal: rowIndex + 1,
        label: `${row.labelPrefix}-${row.labelNumber.toString().padStart(3, '0')}`,
        title: typeof row.metadata?.unitTitle === 'string' ? row.metadata.unitTitle : '',
        height: 6 + Math.round(Math.min(1, mass / 160) * 8),
        cells: witnesses.map((witness, witnessIndex) => {
          const text = resolveMappingText(row, witness.id, lookups.get(witness.id) || new Map())
          const state = getLedgerSafeCellState(row, witness.id)
          return {
            rowId: row.id,
            witnessId: witness.id,
            state: state === 'mapped' ? 'ink' as const : state === 'excluded' ? 'lacuna' as const : 'not-carried' as const,
            fill: state === 'mapped' ? Math.max(0.08, Math.min(1, lengths[witnessIndex] / rowMax)) : 0,
            text,
          }
        }),
      }
    })
}

export const packCameraLucidaLeaves = (
  rows: AnchoredRow[],
  cameraRows: CameraLucidaRow[],
  budget = CAMERA_LUCIDA_LEAF_BUDGET,
  sceneMetadataByRow: Map<string, AnchoredRowSceneMetadata> = new Map(),
): CameraLucidaLeaf[] => {
  const leafBudget = normalizeCameraLucidaLeafBudget(budget)
  const cameraRowById = new Map(cameraRows.map(row => [row.id, row]))
  const groups: Array<{
    sectionId: string
    parentSectionId?: string
    source: CameraLucidaLeaf['source']
    kind: CameraLucidaLeaf['kind']
    title: string
    arabicTitle?: string
    rowIds: string[]
  }> = []
  let current: typeof groups[number] | null = null

  ;[...rows].sort((left, right) => left.position - right.position).forEach((row, index) => {
    const scene = sceneMetadataForRow(row) || sceneMetadataByRow.get(row.id) || null
    const sectionId = sceneIdFor(scene, 'full-collation')
    if (!current || current.sectionId !== sectionId) {
      current = {
        sectionId,
        parentSectionId: parentSceneIdFor(scene),
        source: scene ? 'legacy-scene' : 'fallback',
        kind: scene ? 'scene' : 'unclassified',
        title: String(scene?.title || (sectionId === 'full-collation' ? 'Full collation' : `Scene ${groups.length + 1}`)),
        arabicTitle: typeof scene?.arabicTitle === 'string' ? scene.arabicTitle : undefined,
        rowIds: [],
      }
      groups.push(current)
    }
    current.rowIds.push(row.id || `row-${index + 1}`)
  })

  const leafParts = groups.flatMap(group => {
    const chunks: string[][] = []
    for (let index = 0; index < group.rowIds.length; index += leafBudget) {
      chunks.push(group.rowIds.slice(index, index + leafBudget))
    }
    return chunks.map(rowIds => ({
      sectionId: group.sectionId,
      parentSectionId: group.parentSectionId,
      kind: group.kind,
      source: group.source,
      structurePath: [],
      depth: group.parentSectionId ? 1 : 0,
      narrativeLevel: group.parentSectionId ? 1 : 0,
      title: group.title,
      arabicTitle: group.arabicTitle,
      rows: rowIds.map(rowId => cameraRowById.get(rowId)).filter((row): row is CameraLucidaRow => Boolean(row)),
    }))
  })

  const partCountBySectionId = new Map<string, number>()
  leafParts.forEach(part => {
    partCountBySectionId.set(part.sectionId, (partCountBySectionId.get(part.sectionId) || 0) + 1)
  })
  const nextPartIndexBySectionId = new Map<string, number>()
  return leafParts.map(part => {
    const splitIndex = nextPartIndexBySectionId.get(part.sectionId) || 0
    const splitCount = partCountBySectionId.get(part.sectionId) || 1
    nextPartIndexBySectionId.set(part.sectionId, splitIndex + 1)
    return {
      ...part,
      id: splitCount > 1 ? `${part.sectionId}::${splitIndex + 1}` : part.sectionId,
      splitIndex,
      splitCount,
    }
  })
}

export const packCameraLucidaStructuralLeaves = (
  rows: AnchoredRow[],
  cameraRows: CameraLucidaRow[],
  structuralLayer: ChapterStructuralRangeLayer,
  budget = CAMERA_LUCIDA_LEAF_BUDGET,
): CameraLucidaLeaf[] | null => {
  const leafBudget = normalizeCameraLucidaLeafBudget(budget)
  const orderedRows = [...rows].sort((left, right) => (
    left.position - right.position
    || left.labelNumber - right.labelNumber
    || left.id.localeCompare(right.id)
  ))
  const rowOrderById = buildStructuralRowOrder(orderedRows)
  const usableRanges = structuralLayer.ranges.filter(range => {
    if (range.status !== 'confirmed' || !range.kind) return false
    const start = rowOrderById.get(range.startRowId)
    const end = rowOrderById.get(range.endRowId)
    return start !== undefined && end !== undefined && start <= end
  })
  if (usableRanges.length === 0) return null

  const usableLayer: ChapterStructuralRangeLayer = {
    ...structuralLayer,
    ranges: usableRanges,
  }
  const groups: Array<{
    pathKey: string
    sectionId: string
    parentSectionId?: string
    kind: CameraLucidaLeaf['kind']
    structurePath: CameraLucidaStructurePathEntry[]
    depth: number
    narrativeLevel: number
    title: string
    rowIds: string[]
  }> = []
  let current: typeof groups[number] | null = null

  orderedRows.forEach(row => {
    const context = buildStructuralRangeContext({
      layer: usableLayer,
      rowId: row.id,
      rowOrderById,
    })
    const active = context.activeRangePath[context.activeRangePath.length - 1] || null
    const structurePath: CameraLucidaStructurePathEntry[] = context.activeRangePath.map(entry => ({
      id: entry.id,
      title: entry.title,
      kind: entry.kind,
      depth: entry.depth,
      narrativeLevel: entry.narrativeLevel,
    }))
    const pathKey = structurePath.length > 0
      ? structurePath.map(entry => entry.id).join('\u001f')
      : 'unclassified'

    if (!current || current.pathKey !== pathKey) {
      current = {
        pathKey,
        sectionId: active?.id || `unclassified:${row.id}`,
        parentSectionId: active?.parentRangeId || undefined,
        kind: active?.kind || 'unclassified',
        structurePath,
        depth: active?.depth || 0,
        narrativeLevel: active?.narrativeLevel || 0,
        title: active?.title || 'Unclassified passage',
        rowIds: [],
      }
      groups.push(current)
    }
    current.rowIds.push(row.id)
  })

  const cameraRowById = new Map(cameraRows.map(row => [row.id, row]))
  const leafParts = groups.flatMap(group => {
    const chunks: string[][] = []
    for (let index = 0; index < group.rowIds.length; index += leafBudget) {
      chunks.push(group.rowIds.slice(index, index + leafBudget))
    }
    return chunks.map(rowIds => ({
      sectionId: group.sectionId,
      parentSectionId: group.parentSectionId,
      kind: group.kind,
      source: 'structural-range' as const,
      structurePath: group.structurePath,
      depth: group.depth,
      narrativeLevel: group.narrativeLevel,
      title: group.title,
      rows: rowIds.map(rowId => cameraRowById.get(rowId)).filter((row): row is CameraLucidaRow => Boolean(row)),
    }))
  })
  const partCountBySectionId = new Map<string, number>()
  leafParts.forEach(part => {
    partCountBySectionId.set(part.sectionId, (partCountBySectionId.get(part.sectionId) || 0) + 1)
  })
  const nextPartIndexBySectionId = new Map<string, number>()

  return leafParts.map(part => {
    const splitIndex = nextPartIndexBySectionId.get(part.sectionId) || 0
    const splitCount = partCountBySectionId.get(part.sectionId) || 1
    nextPartIndexBySectionId.set(part.sectionId, splitIndex + 1)
    return {
      ...part,
      id: splitCount > 1 ? `${part.sectionId}::${splitIndex + 1}` : part.sectionId,
      splitIndex,
      splitCount,
    }
  })
}

export const cameraLucidaVisibleWindow = (leaves: CameraLucidaLeaf[], leafIndex: number): number[] => {
  const leaf = leaves[leafIndex]
  if (!leaf) return []
  if (leaf.source === 'structural-range') return [leafIndex]
  const topIndex = leaf.parentSectionId
    ? leaves.findIndex(candidate => candidate.sectionId === leaf.parentSectionId && !candidate.parentSectionId)
    : leafIndex
  const resolvedTopIndex = topIndex >= 0 ? topIndex : leafIndex
  const topSectionId = leaves[resolvedTopIndex].sectionId
  return [
    resolvedTopIndex,
    ...leaves.flatMap((candidate, index) => candidate.parentSectionId === topSectionId ? [index] : []),
  ].filter((index, position, values) => values.indexOf(index) === position).sort((left, right) => left - right)
}

export const cameraLucidaScopeRowsInVisibleLeaves = (
  rowIds: readonly string[],
  rowLeafIndex: ReadonlyMap<string, number>,
  visibleLeafIndexes: readonly number[],
): string[] => {
  const visibleLeaves = new Set(visibleLeafIndexes)
  return rowIds.filter(rowId => {
    const leafIndex = rowLeafIndex.get(rowId)
    return leafIndex !== undefined && visibleLeaves.has(leafIndex)
  })
}

export const cameraLucidaWitnessRuns = (allWitnessIds: string[], witnessIds: string[]): Array<[number, number]> => {
  const columns = witnessIds
    .map(witnessId => allWitnessIds.indexOf(witnessId))
    .filter(column => column >= 0)
    .sort((left, right) => left - right)
  const runs: Array<[number, number]> = []
  columns.forEach(column => {
    const previous = runs[runs.length - 1]
    if (!previous || column > previous[1] + 1) runs.push([column, column])
    else previous[1] = column
  })
  return runs
}

export const cameraLucidaMatteRuns = (allWitnessIds: string[], witnessIds: string[]): Array<[number, number]> => {
  const runs = cameraLucidaWitnessRuns(allWitnessIds, witnessIds)
  if (runs.length <= 2) return runs
  return [[runs[0][0], runs[runs.length - 1][1]]]
}

const activeCharacters = (layer?: ChapterCharacterLayer): CameraLucidaCharacter[] => (
  (layer?.characters || [])
    .filter(character => character.status !== 'rejected')
    .map(character => ({
      id: character.character_id,
      name: character.display_name,
      color: getCameraLucidaCharacterColor(character.character_id),
    }))
)

const characterIdForNote = (note: CharacterLayerNote): string => {
  if (typeof note.metadata?.character_id === 'string') return note.metadata.character_id
  return note.anchor.kind === 'character' ? note.anchor.characterId : ''
}

const buildCharacterSpans = (layer?: ChapterCharacterLayer): CameraLucidaCharacterSpan[] => {
  if (!layer) return []
  const presence = layer.occurrences
    .filter(occurrence => occurrence.status !== 'rejected')
    .map(occurrence => ({
      id: occurrence.occurrence_id,
      characterId: occurrence.character_id,
      rowIds: [occurrence.locator.rowId],
      kind: 'presence' as const,
    }))
  const speakers = layer.notes.flatMap(note => {
    const characterId = characterIdForNote(note)
    return note.metadata?.character_index_layer === 'speaker' && characterId && note.anchor.kind === 'span'
      ? [{ id: note.note_id, characterId, rowIds: [note.anchor.locator.rowId], kind: 'speaker' as const }]
      : []
  })
  return [...presence, ...speakers]
}

export const buildCameraLucidaPlateModel = ({
  rows,
  witnesses,
  characterLayer,
  sceneLayer,
  structuralLayer,
}: {
  rows: AnchoredRow[]
  witnesses: Witness[]
  characterLayer?: ChapterCharacterLayer
  sceneLayer?: ChapterSceneLayer
  structuralLayer?: ChapterStructuralRangeLayer
}): CameraLucidaPlateModel => {
  const cameraRows = createCameraRows(rows, witnesses)
  const structuralLeaves = structuralLayer
    ? packCameraLucidaStructuralLeaves(rows, cameraRows, structuralLayer)
    : null
  const sceneMetadata = structuralLeaves
    ? new Map<string, AnchoredRowSceneMetadata>()
    : canonicalSceneMetadataByRow({ rows, witnesses, sceneLayer })
  const cameraWitnesses: CameraLucidaWitness[] = witnesses.map(witness => ({
    id: witness.id,
    siglum: witness.sigla,
    secondaryLabel: witness.shelfmark || witness.library || undefined,
  }))
  return {
    witnesses: cameraWitnesses,
    rows: cameraRows,
    leaves: structuralLeaves
      || packCameraLucidaLeaves(rows, cameraRows, CAMERA_LUCIDA_LEAF_BUDGET, sceneMetadata),
    characters: activeCharacters(characterLayer),
    characterSpans: buildCharacterSpans(characterLayer),
    characterLayer,
  }
}

export const createCameraLucidaInitialState = (): CameraLucidaSessionState => ({
  question: '',
  status: 'standing by',
  playing: false,
  scope: null,
  suggestion: null,
  overlay: null,
  findings: [],
  coverageRowIds: [],
  exchange: [],
  live: 'Ask, and watch where it reads.',
  activity: { main: 'THE PLATE IS SET', units: '' },
  citedFindingId: null,
  reentryFindingId: null,
  resumeAfterCitation: false,
  snapshotId: null,
  error: null,
})
